import { useEffect, useRef, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiZoomIn,
  FiZoomOut,
} from "react-icons/fi";

import type { UserGuideImage } from "../../entities/user-guide";
import { CloseButton } from "../CloseButton";
import type { GuideImageRef } from "./guideSupport";

/**
 * Скриншот крупно.
 *
 * Скриншоты снимались с полного экрана, а в колонке статьи они сжаты почти
 * вдвое: подписи на кнопках там не разобрать. Поэтому просмотр нужен
 * по-настоящему, и он единственный оверлей страницы: нативный `<dialog>` сам
 * держит фокус внутри и закрывается по Esc. Стрелки листают скриншоты подряд.
 */
export function GuideLightbox({
  images,
  links,
  index,
  onIndexChange,
  onClose,
}: {
  images: readonly GuideImageRef[];
  links: Readonly<Record<string, UserGuideImage>>;
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [actualSize, setActualSize] = useState(false);
  const current = images[index];
  const link = current === undefined ? undefined : links[current.imageId];

  useEffect(() => {
    const element = dialog.current;
    if (element === null) return;
    // jsdom не умеет showModal; в браузере вызов всегда есть.
    if (typeof element.showModal === "function") {
      if (!element.open) element.showModal();
    } else {
      element.setAttribute("open", "");
    }
    document.body.classList.add("guide-lightbox-open");
    return () => {
      document.body.classList.remove("guide-lightbox-open");
      if (typeof element.close === "function" && element.open) element.close();
    };
  }, []);

  // Другой скриншот открывается по ширине окна, а не в том увеличении, что
  // было у предыдущего.
  useEffect(() => {
    setActualSize(false);
  }, [index]);

  if (current === undefined || link === undefined) return null;

  const go = (step: number) => {
    const next = index + step;
    if (next >= 0 && next < images.length) onIndexChange(next);
  };
  const title =
    current.caption ?? (current.alt === "" ? "Скриншот" : current.alt);

  return (
    <dialog
      ref={dialog}
      className="guide-lightbox"
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Клик по затемнению, а не по содержимому окна.
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") go(-1);
        if (event.key === "ArrowRight") go(1);
      }}
    >
      <div className="guide-lightbox__panel">
        <header className="guide-lightbox__header">
          <div>
            <p className="guide-lightbox__title">{title}</p>
            <p className="guide-lightbox__count" aria-live="polite">
              Скриншот {index + 1} из {images.length}
            </p>
          </div>
          <div className="guide-lightbox__actions">
            <button
              className="guide-lightbox__button"
              type="button"
              onClick={() => go(-1)}
              disabled={index === 0}
              aria-label="Предыдущий скриншот"
            >
              <FiChevronLeft aria-hidden="true" />
            </button>
            <button
              className="guide-lightbox__button"
              type="button"
              onClick={() => go(1)}
              disabled={index === images.length - 1}
              aria-label="Следующий скриншот"
            >
              <FiChevronRight aria-hidden="true" />
            </button>
            <button
              className="guide-lightbox__button"
              type="button"
              onClick={() => setActualSize((value) => !value)}
              aria-pressed={actualSize}
              aria-label="Реальный размер"
              title="Реальный размер"
            >
              {actualSize ? (
                <FiZoomOut aria-hidden="true" />
              ) : (
                <FiZoomIn aria-hidden="true" />
              )}
            </button>
            <CloseButton label="Закрыть просмотр" onClick={onClose} />
          </div>
        </header>
        <div
          className={`guide-lightbox__stage${actualSize ? " guide-lightbox__stage--actual" : ""}`}
        >
          <img
            src={link.url}
            alt={current.alt}
            width={link.width ?? undefined}
            height={link.height ?? undefined}
            onClick={() => setActualSize((value) => !value)}
          />
        </div>
      </div>
    </dialog>
  );
}
